import { runInInjectionContext, Injector } from '@angular/core';
import { MatomoTrackerService } from '../services/matomo/matomo-tracker.service';
import { MatomoAction } from '../services/matomo/matomo-analytics.config';

// Token sources from method parameters are no longer supported to avoid lint
// issues and implicit unused parameter problems in components/tests.

function buildActionPath(basePath: string, tokenPath: string): string {
  const baseSegments = (basePath || '')
    .split('/')
    .map(seg => seg.trim())
    .filter(Boolean);
  const tokenSegments = (tokenPath || '')
    .split('/')
    .map(seg => seg.trim())
    .filter(Boolean);

  if (baseSegments.length === 0) {
    return tokenSegments.join('/');
  }
  if (tokenSegments.length === 0) {
    return baseSegments.join('/');
  }

  let overlapLen = 0;
  for (let k = 1; k <= Math.min(baseSegments.length, tokenSegments.length); k += 1) {
    const baseSlice = baseSegments.slice(-k).map(seg => seg.toLowerCase());
    const tokenSlice = tokenSegments.slice(0, k).map(seg => seg.toLowerCase());
    if (JSON.stringify(baseSlice) === JSON.stringify(tokenSlice)) {
      overlapLen = k;
    }
  }

  return [...baseSegments, ...tokenSegments.slice(overlapLen)].join('/');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function TrackAction<T extends (...args: any[]) => any>(
  action: MatomoAction,
  token?: string
) {
  return function (
    _target: unknown,
    _propertyKey: string,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> | void {
    const originalMethod = descriptor.value;
    if (!originalMethod) return descriptor;

    descriptor.value = function (this: unknown, ...args: Parameters<T>): ReturnType<T> {
      const globalInjector = MatomoTrackerService.injector as Injector & {
        destroyed?: boolean;
        _destroyed?: boolean;
      };
      const isDestroyed = globalInjector?.destroyed || globalInjector?._destroyed;

      if (globalInjector && !isDestroyed) {
        runInInjectionContext(globalInjector, () => {
          try {
            const matomo: MatomoTrackerService | null = globalInjector.get(
              MatomoTrackerService,
              null
            );
            if (matomo) {
              const tokenString = typeof token === 'string' ? token : '';

              if (!tokenString) return;

              const currentCategoryPath = matomo.currentCategoryPath();
              const fullActionPath = buildActionPath(currentCategoryPath, tokenString);
              matomo.reportAction(fullActionPath, action);
            }
          } catch (e) {
            console.warn('[TrackAction] Suppressed analytics error in test:', e);
          }
        });
      }

      return originalMethod.apply(this, args) as ReturnType<T>;
    } as T;
    return descriptor;
  };
}
